import { useCallback, useMemo, useState } from "react";
import { useSavedCharts } from "../../accounts/SavedChartsProvider";
import GuidedTour from "../UI/GuidedTour";
import classes from "../UI/GuidedTour.module.css";

function selectDiagram() {
  // Stable card identities survive duplication/deletion. Pick one suitable live
  // card at launch and use its own controls throughout; never restore a target.
  const handle = document.querySelector('[data-refine-card-id] [data-tour-target="refine-move"]');
  const card = handle?.closest("[data-refine-card-id]") || document.querySelector("[data-refine-card-id]");
  return { card, handle };
}

function refineSteps(signedIn, { card, handle }) {
  const inCard = name => () => card?.querySelector(`[data-tour-target="${name}"]`) || null;
  const noDiagram = "There are no live diagrams to highlight. You can continue the tour or exit; deleted diagrams have not been restored.";
  return [
    {
      target: "refine-workflow", title: "Refine your guitar charts",
      text: "You've generated your scale or arpeggio positions. Now you can choose which diagrams to keep, customise individual fingerings, and prepare your finished chart set.",
    },
    {
      target: "refine-details", title: "Add a title and explanation",
      text: "Give your chart set a title and add optional explanatory text using Markdown formatting. These appear in your finished PDF and published document. Use the page-level Undo and Redo controls to reverse changes to the document details.",
    },
    {
      target: "refine-scale-factor", title: "Adjust your diagram size",
      text: "Control the size of all your diagrams in the finished document. This setting applies to the entire chart set, keeping your diagrams consistently sized.",
    },
    {
      target: "refine-move", title: "Customise your fingerings",
      getTarget: () => handle || inCard("refine-diagram")(),
      text: "Notes with dotted outlines can be moved to an adjacent string while keeping the same pitch. Drag an eligible note to change the fingering, or use the up/down arrow keys when its move handle is focused. Only eligible notes can be repositioned this way." +
        (card && !handle ? " No move handle is currently available on this diagram. Your charts have been left as they are." : ""),
      unavailableText: noDiagram,
    },
    {
      target: "refine-remove", title: "Remove unwanted notes", getTarget: inCard("refine-remove"),
      text: "Select Remove notes to remove individual notes from a diagram. This lets you create simpler fingerings, specialised exercises, or patterns containing only the notes you want to practise. Other notes remain in their existing positions." +
        (card?.querySelector('[data-tour-target="refine-remove"][aria-pressed="true"]') ? " This diagram is already in Remove notes mode; its button currently reads Done." : ""),
      unavailableText: noDiagram,
    },
    {
      target: "refine-history", title: "Undo individual diagram changes", getTarget: inCard("refine-history"),
      text: "Each diagram has its own Undo and Redo controls. You can reverse changes to one fingering without affecting the other diagrams or the document's title and introduction.",
      unavailableText: noDiagram,
    },
    {
      target: "refine-actions", title: "Keep the fingerings you want", getTarget: inCard("refine-actions"),
      text: "Delete diagrams you don't need, or duplicate useful ones to create alternative fingerings. A deleted diagram can be restored during this editing session using its Restore control.",
      unavailableText: noDiagram,
    },
    {
      target: "chart-save", title: "Save your work",
      text: signedIn
        ? "Save your complete chart set, including its title, introduction and customised diagrams. You can return to it later from My Charts."
        : "You can create, refine and download charts without an account. Sign in to save complete, editable chart sets privately and return to them later.",
      unavailableText: "Saving is not available in this version of the site. You can still refine and download your charts.",
    },
    {
      target: "refine-preview", title: "Preview and export your charts",
      text: "Choose Preview to see your finished document without editing controls. From there, you can download a complete PDF or individual SVG and PNG diagrams. You can also open Publish to create a printable, shareable chart page. This completes the Refine tour. Your current work has not been changed.",
    },
  ];
}

export default function RefineQuickTour() {
  const saved = useSavedCharts();
  const [selection, setSelection] = useState(null);
  // Session restoration can finish while the tour is open. Keep the saving
  // explanation current without changing the chosen diagram or tour position.
  const steps = useMemo(() => selection && refineSteps(saved.ready, selection), [saved.ready, selection]);
  const close = useCallback(() => setSelection(null), []);
  return <>
    <button type="button" className={classes.start} onClick={() => setSelection(selectDiagram())}>
      Take a quick tour
    </button>
    {steps && <GuidedTour steps={steps} onClose={close} />}
  </>;
}
